'use client'

import React from 'react'

import { format as formatDate, parseISO } from 'date-fns'
import { twMerge } from 'tailwind-merge'

/**
 * Currency formatter component
 *
 * Displays monetary values with proper formatting and optional compact notation.
 */
export interface CurrencyProps {
  value: number
  compact?: boolean
  className?: string
}

export const Currency: React.FC<CurrencyProps> = ({
  value,
  compact = false,
  className = '',
}) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 0,
  })

  return (
    <span className={twMerge('font-medium tracking-tight', className)}>
      {formatter.format(value)}
    </span>
  )
}

/**
 * Date display component
 *
 * Formats dates in a consistent, readable format.
 */
export interface DateDisplayProps {
  dateStr: string | Date | null
  format?: 'short' | 'medium' | 'long'
  className?: string
}

function parseDisplayDate(value: string | Date): Date {
  if (value instanceof Date) {
    return value
  }

  // Treat date-only strings as local calendar dates so they don't shift
  // backward when formatted in a negative UTC offset.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseISO(value)
  }

  return new Date(value)
}

export const DateDisplay: React.FC<DateDisplayProps> = ({
  dateStr,
  format = 'medium',
  className = '',
}) => {
  // Handle empty, null, or undefined values
  if (!dateStr) {
    return (
      <span className={twMerge('font-medium text-primary-400', className)}>
        —
      </span>
    )
  }

  const date = parseDisplayDate(dateStr)

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return (
      <span className={twMerge('font-medium text-primary-400', className)}>
        —
      </span>
    )
  }

  return (
    <span className={twMerge(className)}>
      {formatDate(date, 'MMM d, yyyy')}
    </span>
  )
}

/**
 * Percentage formatter component
 */
export interface PercentageProps {
  value: number
  decimals?: number
  className?: string
}

export const Percentage: React.FC<PercentageProps> = ({
  value,
  decimals = 1,
  className = '',
}) => {
  // Handle non-numeric values gracefully
  const numValue =
    typeof value === 'number' ? value : parseFloat(String(value)) || 0
  return (
    <span className={twMerge('font-medium', className)}>
      {numValue.toFixed(decimals)}%
    </span>
  )
}

/**
 * Number formatter component (for counts, etc.)
 */
export interface NumberDisplayProps {
  value: number
  compact?: boolean
  className?: string
}

export const NumberDisplay: React.FC<NumberDisplayProps> = ({
  value,
  compact = false,
  className = '',
}) => {
  const formatter = new Intl.NumberFormat('en-US', {
    notation: compact ? 'compact' : 'standard',
  })

  return (
    <span className={twMerge('font-medium', className)}>
      {formatter.format(value)}
    </span>
  )
}
